(() => {
  const tagName = 'agl-consent-preferences';

  if (customElements.get(tagName)) return;

  const CONSENT_PURPOSES = ['preferences', 'analytics', 'marketing'];

  class AglConsentPreferences extends HTMLElement {
    connectedCallback() {
      if (this.dataset.aglConsentFallbackDisabled === 'true') {
        this.hidden = true;
        return;
      }

      this.lifecycleGeneration = (this.lifecycleGeneration || 0) + 1;
      if (this.initialized) {
        AglConsentPreferences.instances.add(this);
        if (AglConsentPreferences.owner && AglConsentPreferences.owner !== this) {
          const owner = AglConsentPreferences.owner;
          if (owner.dialog?.id) this.openButton.setAttribute('aria-controls', owner.dialog.id);
          return;
        }
        this.claimOwnership();
        return;
      }
      this.initialized = true;

      this.banner = this.querySelector('[data-agl-consent-banner]');
      this.dialog = this.querySelector('[data-agl-consent-dialog]');
      this.openButton = this.querySelector('[data-agl-consent-open]');
      this.closeButton = this.querySelector('[data-agl-consent-close]');
      this.manageButton = this.querySelector('[data-agl-consent-manage]');
      this.statuses = [...this.querySelectorAll('[data-agl-consent-status]')];
      [this.bannerStatus, this.dialogStatus] = this.statuses;
      this.actionButtons = [...this.querySelectorAll('[data-agl-consent-action]')];
      this.inputs = new Map(
        [...this.querySelectorAll('[data-agl-consent-purpose]')].map((input) => [
          input.dataset.aglConsentPurpose,
          input,
        ])
      );

      if (
        !this.banner ||
        !this.dialog ||
        !this.openButton ||
        !this.closeButton ||
        !this.manageButton ||
        this.statuses.length === 0
      ) {
        return;
      }

      AglConsentPreferences.instances.add(this);
      this.openButton.addEventListener('click', () => {
        const owner = AglConsentPreferences.owner;
        if (owner?.dialog?.id) this.openButton.setAttribute('aria-controls', owner.dialog.id);
        return owner?.open(this.openButton);
      });

      if (AglConsentPreferences.owner && AglConsentPreferences.owner !== this) {
        const owner = AglConsentPreferences.owner;
        if (owner.dialog?.id) this.openButton.setAttribute('aria-controls', owner.dialog.id);
        return;
      }

      this.claimOwnership();
    }

    claimOwnership() {
      if (AglConsentPreferences.owner && AglConsentPreferences.owner !== this) return;
      if (AglConsentPreferences.owner === this) return;

      AglConsentPreferences.owner = this;
      if (this.dialog.id) this.openButton.setAttribute('aria-controls', this.dialog.id);
      this.portalFallbackSurfaces();

      if (!this.ownerListenersBound) {
        this.ownerListenersBound = true;
        this.closeButton.addEventListener('click', () => {
          this.dialog.close();
        });
        this.dialog.addEventListener('close', () => {
          const restoreBanner = this.restoreBannerOnClose;
          const returnFocus = this.dialogReturnFocus;
          const transferActionError =
            !this.suppressingForNative &&
            !this.tearingDown &&
            this.actionErrorPending &&
            !this.dialogStatus.hidden;
          this.dialogRecoveryPending = false;
          this.setStatus('', false, 'dialog');
          if (restoreBanner) this.banner.hidden = false;
          if (transferActionError) {
            this.banner.hidden = false;
            this.setStatus(this.dataset.errorMessage, true, 'banner');
          }
          this.restoreBannerOnClose = false;
          this.dialogReturnFocus = null;
          if (!this.suppressingForNative && !this.tearingDown) returnFocus?.focus();
        });
        this.manageButton.addEventListener('click', () => this.open(this.manageButton));
        this.actionButtons.forEach((button) => {
          button.addEventListener('click', () => this.updateConsent(button.dataset.aglConsentAction));
        });
        this.inputs.forEach((input) => {
          input.addEventListener('change', () => this.refreshTimedOutConsentActions());
        });
      }

      this.initialization = null;
      if (document.readyState === 'complete') {
        this.initializeFallback();
      } else {
        window.addEventListener('load', () => this.initializeFallback(), { once: true });
      }
    }

    disconnectedCallback() {
      AglConsentPreferences.instances.delete(this);
      if (AglConsentPreferences.owner !== this) return;

      this.lifecycleGeneration = (this.lifecycleGeneration || 0) + 1;
      this.cancelPendingOperations();
      this.resetBusy();
      this.stopPrivacyRetry();
      this.tearingDown = true;
      if (this.dialog.open) this.dialog.close();
      this.tearingDown = false;
      this.actionErrorPending = false;
      this.setStatus('');
      this.banner.remove();
      this.dialog.remove();
      AglConsentPreferences.owner = null;
      const replacement = AglConsentPreferences.instances.values().next().value;
      replacement?.claimOwnership();
    }

    isActive(generation = this.lifecycleGeneration) {
      return AglConsentPreferences.owner === this && generation === this.lifecycleGeneration;
    }

    cancelPendingOperations() {
      const privacyLoadOperation = this.privacyLoadOperation;
      if (privacyLoadOperation?.timeout) window.clearTimeout(privacyLoadOperation.timeout);
      if (this.privacyLoadOperation === privacyLoadOperation) this.privacyLoadOperation = null;
      this.privacyPromise = null;
      privacyLoadOperation?.reject(new Error('Consent component lifecycle ended'));

      const lifecycleError = new Error('Consent component lifecycle ended');
      if (this.consentWriteGroup) {
        this.cancelConsentWriteGroup(this.consentWriteGroup, lifecycleError);
      } else if (this.consentWriteOperation) {
        const operation = this.consentWriteOperation;
        this.consentWriteOperation = null;
        if (operation.timeout) window.clearTimeout(operation.timeout);
        operation.superseded = true;
        this.endBusy(operation.busyToken);
        operation.reject?.(lifecycleError);
      }
      this.consentUpdatePending = false;
    }

    cancelConsentWriteGroup(group, error) {
      if (!group || group.cancelled) return;
      group.cancelled = true;
      group.outcome = 'cancelled';
      if (this.consentWriteGroup === group) {
        this.consentWriteGroup = null;
        this.consentWriteOperation = null;
        this.consentUpdatePending = false;
      }
      group.operations.forEach((operation) => {
        if (operation.timeout) window.clearTimeout(operation.timeout);
        operation.superseded = true;
        this.endBusy(operation.busyToken);
        operation.reject?.(error);
      });
    }

    portalFallbackSurfaces() {
      const footerContent = this.closest('.footer-content');
      const host = footerContent?.parentElement || document.body;
      host?.append(this.banner);
      host?.append(this.dialog);
    }

    initializeFallback() {
      if (AglConsentPreferences.owner !== this) return;
      if (this.initialization) return this.initialization;
      const generation = this.lifecycleGeneration;

      this.initialization = (async () => {
        try {
          const privacy = await this.loadCustomerPrivacyApi();
          if (!this.isActive(generation)) return;
          const shouldShow =
            privacy.shouldShowBanner() &&
            !this.hasVisitorDecision(privacy);

          this.banner.hidden = !shouldShow || this.dialog.open;
          if (this.dialog.open) {
            this.syncInputsForPrivacy(privacy);
            if (this.dialogRecoveryPending && !this.actionErrorPending) {
              this.setStatus('', false, 'dialog');
            }
          }
          this.dialogRecoveryPending = false;
          this.stopPrivacyRetry();
        } catch {
          if (!this.isActive(generation)) return;
          this.banner.hidden = false;
          this.schedulePrivacyRetry();
        }
      })();

      return this.initialization;
    }

    reconcileAvailablePrivacy() {
      if (!this.privacyRetryTimer || !window.Shopify?.customerPrivacy) return;

      this.stopPrivacyRetry();
      this.initialization = null;
      this.initializeFallback();
    }

    schedulePrivacyRetry() {
      if (this.privacyRetryTimer) return;

      this.privacyRetryTimer = window.setTimeout(() => {
        this.privacyRetryTimer = null;
        this.initialization = null;
        this.initializeFallback();
      }, 5000);
    }

    stopPrivacyRetry() {
      if (!this.privacyRetryTimer) return;
      window.clearTimeout(this.privacyRetryTimer);
      this.privacyRetryTimer = null;
    }

    open(returnFocus = this.openButton) {
      return this.openFallback(returnFocus);
    }

    async openFallback(returnFocus = this.openButton) {
      if (this.dialog.open) return;
      const generation = this.lifecycleGeneration;

      this.dialogReturnFocus = returnFocus;
      this.restoreBannerOnClose = !this.banner.hidden;
      this.dialogRecoveryPending = false;
      this.banner.hidden = true;
      this.dialog.showModal();
      const busyToken = this.beginBusy();
      if (this.actionErrorPending) this.setStatus(this.dataset.errorMessage, true, 'dialog');
      else this.setStatus('', false, 'dialog');

      try {
        const privacy = await this.loadCustomerPrivacyApi();
        if (!this.isActive(generation)) return;
        this.stopPrivacyRetry();
        this.dialogRecoveryPending = false;
        if (!this.actionErrorPending) this.setStatus('', false, 'dialog');
        this.syncInputsForPrivacy(privacy);
      } catch {
        if (!this.isActive(generation)) return;
        this.dialogRecoveryPending = true;
        this.schedulePrivacyRetry();
      } finally {
        if (this.isActive(generation)) {
          this.endBusy(busyToken);
          this.refreshTimedOutConsentActions();
        }
      }
    }

    loadCustomerPrivacyApi() {
      if (window.Shopify?.customerPrivacy) {
        return Promise.resolve(window.Shopify.customerPrivacy);
      }

      if (this.privacyPromise) return this.privacyPromise;

      const operation = {reject: null, script: null, timeout: null};
      const promise = new Promise((resolve, reject) => {
        let settled = false;
        let onLoad;
        let onError;

        const finish = (error, keepScript = false) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(operation.timeout);
          operation.script?.removeEventListener('load', onLoad);
          operation.script?.removeEventListener('error', onError);
          if (this.privacyLoadOperation === operation) this.privacyLoadOperation = null;
          if (operation.script && (error || !keepScript)) operation.script.remove();
          if (error) reject(error);
          else resolve(window.Shopify.customerPrivacy);
        };
        const startFirstPartyFallback = () => {
          if (settled || operation.script) return;
          if (window.Shopify?.customerPrivacy) {
            finish();
            return;
          }

          const runtimeUrl = this.dataset.aglCustomerPrivacyRuntimeUrl?.trim();
          if (!runtimeUrl) {
            finish(new Error('Shopify Customer Privacy runtime URL is unavailable'));
            return;
          }

          window.clearTimeout(operation.timeout);
          try {
            const existing = document.querySelector(
              'script[data-agl-customer-privacy-runtime]'
            );
            const script = existing || document.createElement('script');
            operation.script = script;
            onLoad = () => {
              if (window.Shopify?.customerPrivacy) finish(null, true);
              else finish(new Error('Shopify Customer Privacy runtime did not initialize'));
            };
            onError = () => finish(new Error('Shopify Customer Privacy runtime failed to load'));
            script.addEventListener('load', onLoad, {once: true});
            script.addEventListener('error', onError, {once: true});
            operation.timeout = window.setTimeout(
              () => {
                if (window.Shopify?.customerPrivacy) finish();
                else finish(new Error('Shopify Customer Privacy runtime load timed out'));
              },
              5000
            );

            if (existing) return;
            script.src = runtimeUrl;
            script.async = true;
            script.setAttribute('data-agl-customer-privacy-runtime', '');
            document.head.append(script);
          } catch (error) {
            finish(error);
          }
        };

        operation.reject = (error) => finish(error);
        operation.timeout = window.setTimeout(startFirstPartyFallback, 5000);
        this.privacyLoadOperation = operation;

        if (!window.Shopify?.loadFeatures) {
          startFirstPartyFallback();
          return;
        }

        try {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (!error && window.Shopify.customerPrivacy) finish();
              else startFirstPartyFallback();
            }
          );
        } catch {
          startFirstPartyFallback();
        }
      });

      const trackedPromise = promise.catch((error) => {
        if (this.privacyPromise === trackedPromise) this.privacyPromise = null;
        throw error;
      });
      this.privacyPromise = trackedPromise;

      return this.privacyPromise;
    }

    syncInputs(consent) {
      CONSENT_PURPOSES.forEach((purpose) => {
        const input = this.inputs.get(purpose);
        if (input) input.checked = consent?.[purpose] === 'yes' || consent?.[purpose] === true;
      });
    }

    syncInputsForPrivacy(privacy) {
      const operation = this.consentWriteOperation;
      const group = this.consentWriteGroup;
      const choices = operation?.timedOut && group && !group.cancelled ? group.choices : null;
      this.syncInputs(choices || privacy.currentVisitorConsent());
    }

    hasVisitorDecision(privacy) {
      try {
        if (typeof privacy.getTrackingConsent !== 'function') return false;
        // Granular defaults are "no" before interaction; the aggregate state distinguishes it.
        return ['yes', 'no'].includes(privacy.getTrackingConsent());
      } catch {
        return false;
      }
    }

    shopifyPersistenceMetadata() {
      const features = document.getElementById('shopify-features');
      const accessToken = features ? JSON.parse(features.textContent || '{}').accessToken : null;
      const rootDomain = window.location?.hostname;

      if (!accessToken || !rootDomain) {
        throw new Error('Shopify storefront consent context is unavailable');
      }

      return {
        headlessStorefront: true,
        storefrontAccessToken: accessToken,
        storefrontRootDomain: rootDomain,
        checkoutRootDomain: rootDomain,
      };
    }

    submitTrackingConsent(privacy, payload, callback) {
      return privacy.setTrackingConsent(payload, callback);
    }

    payloadFor(action) {
      const force = action === 'accept' ? true : action === 'reject' ? false : null;

      return Object.fromEntries(
        CONSENT_PURPOSES.map((purpose) => [
          purpose,
          force === null ? Boolean(this.inputs.get(purpose)?.checked) : force,
        ])
      );
    }

    sameConsentChoices(left, right) {
      return CONSENT_PURPOSES.every((purpose) => left?.[purpose] === right?.[purpose]);
    }

    confirmsConsentChoices(privacy, choices) {
      try {
        const trackingConsent = privacy.getTrackingConsent();
        if (!['yes', 'no'].includes(trackingConsent)) return false;
        const currentConsent = privacy.currentVisitorConsent();
        return CONSENT_PURPOSES.every((purpose) => {
          const value = currentConsent?.[purpose];
          return ['yes', 'no'].includes(value) && (value === 'yes') === choices[purpose];
        });
      } catch {
        return false;
      }
    }

    releaseTimedOutConsentControls(operation) {
      if (!operation?.timedOut) return;
      this.endBusy(operation.busyToken);
      this.refreshTimedOutConsentActions();
    }

    refreshTimedOutConsentActions() {
      const operation = this.consentWriteOperation;
      const group = this.consentWriteGroup;
      if (!operation?.timedOut || !group || group.cancelled || this.busyTokens?.size) return;
      this.actionButtons.forEach((button) => {
        button.disabled = !this.sameConsentChoices(
          this.payloadFor(button.dataset.aglConsentAction),
          group.choices
        );
      });
    }

    unresolvedConsentWriteOperations(group) {
      return [...group.operations].filter((operation) => !operation.callbackHandled);
    }

    pruneConsentWriteOperations(group, retainedOperation) {
      group.operations.forEach((operation) => {
        if (operation === retainedOperation) return;
        if (operation.timeout) window.clearTimeout(operation.timeout);
        group.operations.delete(operation);
        operation.superseded = true;
        operation.callbackHandled = true;
        operation.group = null;
        this.endBusy(operation.busyToken);
        operation.resolve?.();
        operation.resolve = null;
        operation.reject = null;
      });
    }

    settleConsentWriteGroup(group, privacy) {
      if (
        !group ||
        group.cancelled ||
        group.settled ||
        this.consentWriteGroup !== group
      ) {
        return;
      }

      group.settled = true;
      group.outcome = 'success';
      this.consentWriteGroup = null;
      this.consentWriteOperation = null;
      this.consentUpdatePending = false;
      group.operations.forEach((operation) => {
        if (operation.timeout) window.clearTimeout(operation.timeout);
        operation.superseded = true;
        this.endBusy(operation.busyToken);
        operation.resolve?.();
      });
      this.completeConsentUpdate(privacy);
    }

    handleConsentWriteCallback(operation, privacy, error, generation) {
      if (operation.callbackHandled) return;
      if (operation.timeout) window.clearTimeout(operation.timeout);
      operation.callbackHandled = true;
      const group = operation.group;

      if (
        !this.isActive(generation) ||
        !group ||
        group.cancelled ||
        group.settled ||
        this.consentWriteGroup !== group
      ) {
        operation.resolve?.();
        return;
      }

      if (!error) {
        this.settleConsentWriteGroup(group, privacy);
        return;
      }

      operation.superseded = true;
      const unresolved = this.unresolvedConsentWriteOperations(group);
      if (unresolved.length) {
        if (this.consentWriteOperation === operation) {
          const candidates = [...unresolved].reverse();
          const retryable = candidates.find((candidate) => candidate.timedOut) || candidates[0];
          this.consentWriteOperation = retryable;
          this.consentUpdatePending = true;
          this.endBusy(operation.busyToken);
          if (retryable?.timedOut) this.releaseTimedOutConsentControls(retryable);
          operation.reject?.(new Error('Shopify Customer Privacy API rejected the update'));
        } else {
          operation.resolve?.();
        }
        return;
      }

      group.settled = true;
      group.outcome = 'failure';
      this.consentWriteGroup = null;
      this.consentWriteOperation = null;
      this.consentUpdatePending = false;
      group.operations.forEach((attempt) => {
        if (attempt.timeout) window.clearTimeout(attempt.timeout);
        attempt.superseded = true;
        this.endBusy(attempt.busyToken);
      });
      if (operation.timedOut) {
        this.setConsentError(group.statusSurface, true);
        operation.resolve?.();
      } else {
        operation.reject?.(new Error('Shopify Customer Privacy API rejected the update'));
      }
    }

    async updateConsent(action) {
      if (!['accept', 'reject', 'save'].includes(action)) return;
      const choices = this.payloadFor(action);
      let group = this.consentWriteGroup;
      if (this.consentUpdatePending) {
        const pendingOperation = this.consentWriteOperation;
        if (
          !pendingOperation?.timedOut ||
          !group ||
          !this.sameConsentChoices(choices, group.choices)
        ) {
          return;
        }
        this.pruneConsentWriteOperations(group, pendingOperation);
        pendingOperation.superseded = true;
        this.endBusy(pendingOperation.busyToken);
      }
      if (!group) {
        group = {
          choices,
          operations: new Set(),
          automaticRetryCount: 0,
          statusSurface: 'banner',
          settled: false,
          cancelled: false,
          outcome: null,
        };
        this.consentWriteGroup = group;
      }

      const generation = this.lifecycleGeneration;
      this.consentUpdatePending = true;
      this.actionErrorPending = false;
      const statusSurface = this.dialog.open ? 'dialog' : 'banner';
      group.statusSurface = statusSurface;
      let submitted = false;
      let privacy = null;
      const busyToken = this.beginBusy();
      const operation = {
        reject: null,
        resolve: null,
        timeout: null,
        timedOut: false,
        superseded: false,
        callbackHandled: false,
        choices,
        busyToken,
        group,
      };
      group.operations.add(operation);
      this.consentWriteOperation = operation;
      this.setStatus('');

      try {
        privacy = await this.loadCustomerPrivacyApi();
        if (
          !this.isActive(generation) ||
          group.cancelled ||
          group.settled ||
          this.consentWriteGroup !== group
        ) {
          return;
        }
        const payload = {
          ...choices,
          ...this.shopifyPersistenceMetadata(),
        };

        await new Promise((resolve, reject) => {
          operation.resolve = resolve;
          operation.reject = reject;
          operation.timeout = window.setTimeout(
            () => {
              operation.timedOut = true;
              if (this.consentWriteOperation === operation) {
                this.releaseTimedOutConsentControls(operation);
              }
              reject(new Error('Shopify Customer Privacy API timed out'));
            },
            5000
          );

          try {
            submitted = true;
            this.submitTrackingConsent(privacy, payload, (error) => {
              this.handleConsentWriteCallback(operation, privacy, error, generation);
            });
          } catch (error) {
            this.handleConsentWriteCallback(operation, privacy, error, generation);
          }
        });

        if (!this.isActive(generation) || group.cancelled || group.settled) return;
      } catch {
        if (!this.isActive(generation)) return;
        if (group.cancelled || group.outcome === 'success') return;
        if (this.consentWriteGroup !== group && !group.settled) return;
        if (
          submitted &&
          operation.timedOut &&
          !group.settled &&
          this.consentWriteGroup === group &&
          this.consentWriteOperation === operation
        ) {
          if (this.confirmsConsentChoices(privacy, group.choices)) {
            this.settleConsentWriteGroup(group, privacy);
            return;
          }
          if (group.automaticRetryCount < 1) {
            group.automaticRetryCount += 1;
            await this.updateConsent(action);
            return;
          }
        }
        if (!submitted) {
          operation.callbackHandled = true;
          group.operations.delete(operation);
          this.endBusy(operation.busyToken);
          const unresolved = this.unresolvedConsentWriteOperations(group);
          if (unresolved.length) {
            const candidates = [...unresolved].reverse();
            const retryable = candidates.find((candidate) => candidate.timedOut) || candidates[0];
            this.consentWriteOperation = retryable;
            this.consentUpdatePending = true;
            if (retryable?.timedOut) this.releaseTimedOutConsentControls(retryable);
          } else {
            group.settled = true;
            group.outcome = 'failure';
            this.consentWriteGroup = null;
            this.consentWriteOperation = null;
            this.consentUpdatePending = false;
          }
        }
        this.setConsentError(statusSurface, true);
      } finally {
        if (this.isActive(generation) && !this.consentUpdatePending) this.endBusy(busyToken);
      }
    }

    completeConsentUpdate(privacy) {
      if (!this.isActive()) return;
      this.actionErrorPending = false;
      this.setStatus('');
      try {
        this.syncInputs(privacy.currentVisitorConsent());
      } catch {
        // The successful Shopify callback remains authoritative.
      }

      this.banner.hidden = true;
      this.stopPrivacyRetry();
      if (this.dialog.open) {
        this.restoreBannerOnClose = false;
        this.dialog.close();
      }
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.openButton.disabled = busy;
      this.manageButton.disabled = busy;
      this.actionButtons.forEach((button) => {
        button.disabled = busy;
      });
      this.inputs.forEach((input) => {
        input.disabled = busy;
      });
    }

    beginBusy() {
      if (!this.busyTokens) this.busyTokens = new Set();
      const token = Symbol('consent-operation');
      this.busyTokens.add(token);
      this.setBusy(true);
      return token;
    }

    endBusy(token) {
      this.busyTokens?.delete(token);
      this.setBusy(Boolean(this.busyTokens?.size));
    }

    resetBusy() {
      this.busyTokens?.clear();
      this.setBusy(false);
    }

    setStatus(message, isError = false, surface = 'all') {
      const statuses =
        surface === 'banner'
          ? [this.bannerStatus]
          : surface === 'dialog'
            ? [this.dialogStatus]
            : this.statuses;

      statuses.filter(Boolean).forEach((status) => {
        status.textContent = message;
        status.hidden = !message;
        status.toggleAttribute('data-error', isError);
      });
    }

    setConsentError(preferredSurface, persistUntilAction = false) {
      if (!this.isActive()) return;
      const surface = preferredSurface === 'dialog' && !this.dialog.open ? 'banner' : preferredSurface;
      if (persistUntilAction) this.actionErrorPending = true;
      if (surface === 'banner') this.banner.hidden = false;
      this.setStatus(this.dataset.errorMessage, true, surface);
    }
  }

  AglConsentPreferences.instances = new Set();
  customElements.define(tagName, AglConsentPreferences);
})();
